import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, ArrowRight, CheckCircle2, Lightbulb, Video, BarChart3, Gauge } from "lucide-react";
import BrandLogo, { BrandIcon } from "./BrandLogo";
import { RadialScore, MetricCard, SectionHeading } from "./results/ResultsPrimitives";
import { SkeletonList } from "./ReportSkeleton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLOW = [["Video", Video], ["Análisis", BarChart3], ["Score", Gauge], ["Insights", Lightbulb], ["Recomendaciones", Sparkles]];

function ExampleReport({ ex, index }) {
  const breakdown = Object.entries(ex.viral_breakdown || {}).slice(0, 8);
  return (
    <article data-testid={`example-card-${ex.id}`} className="vt-card bg-brand-card overflow-hidden">
      <div className="grid lg:grid-cols-[minmax(0,1fr)_auto] gap-6 p-5 md:p-7">
        <div className="min-w-0">
          <div className="flex items-start gap-4">
            {ex.thumbnail ? (
              <img src={ex.thumbnail} alt={ex.title} className="w-32 h-20 md:w-44 md:h-24 object-cover rounded-xl border border-white/10 shadow-lg shrink-0" />
            ) : (
              <span className="w-20 h-20 rounded-xl border border-purple-400/30 bg-purple-500/10 text-purple-300 flex items-center justify-center shrink-0"><Sparkles className="w-8 h-8" /></span>
            )}
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand-muted">Informe real #{index + 1}</p>
              <h2 className="text-lg md:text-xl font-bold text-vt-ink leading-snug line-clamp-2">{ex.title}</h2>
              {ex.channel && <p className="text-sm text-brand-text2 mt-0.5 truncate">{ex.channel}</p>}
            </div>
          </div>

          {breakdown.length > 0 && (
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              {breakdown.map(([k, v], i) => <MetricCard key={k} name={k} value={v} index={i} />)}
            </div>
          )}

          {(ex.strengths?.length > 0 || ex.recommendations_count > 0) && (
            <div className="mt-5 grid sm:grid-cols-2 gap-2.5">
              {ex.strengths?.map((s) => (
                <div key={s.substring(0, 40)} className="flex items-start gap-2 rounded-xl border border-white/10 bg-brand-elevated/60 px-3 py-2.5">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-300" />
                  <p className="text-sm text-brand-text2 line-clamp-2">{s}</p>
                </div>
              ))}
              {ex.recommendations_count > 0 && (
                <div className="flex items-center gap-2 rounded-xl border border-purple-400/25 bg-purple-500/10 px-3 py-2.5">
                  <Lightbulb className="w-4 h-4 shrink-0 text-purple-300" />
                  <p className="text-sm text-brand-text2">
                    <span className="font-bold text-vt-ink">{ex.recommendations_count} recomendaciones</span> accionables
                    {ex.estimated_total_improvement && <> · mejora estimada <span className="font-bold vt-gradient-text-cool">{ex.estimated_total_improvement}</span></>}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col items-center justify-center gap-4 lg:border-l lg:border-white/10 lg:pl-6">
          <RadialScore id={`ex-${ex.id}`} value={ex.viral_score} size={150} stroke={11} label="Potencial viral" />
          {ex.growth_potential_score && (
            <div className="flex items-center gap-2 text-xs text-brand-text2">
              <RadialScore id={`exg-${ex.id}`} value={ex.growth_potential_score} size={44} stroke={5} compact />
              Potencial de crecimiento
            </div>
          )}
          <a href={`/share/${ex.id}`} data-testid={`example-open-${ex.id}`} className="vt-btn-primary vt-cta-glow text-sm w-full justify-center">
            Ver informe completo <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </article>
  );
}

export default function ExamplesPage() {
  const [examples, setExamples] = useState(null);

  useEffect(() => {
    document.title = "Informes de Ejemplo | ViralTubeAI — Análisis de Videos de YouTube con IA";
    axios.get(`${API}/examples`).then((r) => setExamples(r.data)).catch(() => setExamples([]));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-purple-100 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <a href="/" className="flex items-center gap-2 min-w-0"><BrandIcon size="sm" /><span className="hidden sm:block"><BrandLogo size="sm" textOnly /></span></a>
          <a href="/" data-testid="examples-back-link" className="vt-btn-primary vt-cta-glow text-sm px-4 py-2 rounded-full">
            <Sparkles className="w-4 h-4" /> Analizar mi video
          </a>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <section className="vt-dark-zone relative overflow-hidden rounded-3xl bg-gradient-to-b from-brand-bg via-[#140f3a] to-brand-bg text-white px-6 py-12 md:py-16 text-center border border-white/10 mb-12">
          <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-purple-600/35 blur-[130px]" />
          <div className="pointer-events-none absolute right-[10%] top-[15%] w-[220px] h-[220px] rounded-full bg-cyan-500/20 blur-[110px]" />
          <div className="relative">
            <p className="inline-flex items-center gap-2 vt-glass px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest text-purple-200 mb-5"><Sparkles className="w-3.5 h-3.5" /> Sin registro · Informes reales</p>
            <h1 className="text-4xl sm:text-5xl font-extrabold mb-4">
              Informes de <span className="vt-gradient-text vt-gradient-text-glow">Ejemplo</span>
            </h1>
            <p className="text-brand-text2 max-w-2xl mx-auto">
              Así se ve un análisis completo de ViralTubeAI: potencial viral, SEO, retención, hooks,
              storytelling y oportunidades de crecimiento generados con inteligencia artificial.
              Explora estos informes reales y luego analiza tu propio video gratis.
            </p>
            <div data-testid="examples-flow" className="flex flex-wrap items-center justify-center gap-2 mt-7">
              {FLOW.map(([label, Icon], i) => (
                <div key={label} className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs sm:text-sm font-semibold text-white"><Icon className="w-4 h-4 text-purple-300" /> {label}</span>
                  {i < FLOW.length - 1 && <ArrowRight className="w-4 h-4 text-purple-400" />}
                </div>
              ))}
            </div>
          </div>
        </section>

        {!examples && <div data-testid="examples-loading"><SkeletonList rows={3} /></div>}

        {examples && examples.length === 0 && (
          <div data-testid="examples-empty" className="vt-empty bg-brand-card">
            <Sparkles className="w-10 h-10 text-purple-400 mx-auto mb-3" />
            <p className="text-gray-600">Pronto publicaremos informes de ejemplo. Mientras tanto, <a href="/" className="text-purple-600 font-semibold underline">analiza tu propio video gratis</a>.</p>
          </div>
        )}

        {examples && examples.length > 0 && (
          <>
            <SectionHeading eyebrow="Demostración del producto" title="Informes públicos generados por ViralTubeAI" icon={BarChart3} />
            <div data-testid="examples-grid" className="mt-6 space-y-6">
              {examples.map((ex, i) => <ExampleReport key={ex.id} ex={ex} index={i} />)}
            </div>
          </>
        )}

        <div className="text-center mt-14">
          <a href="/" data-testid="examples-cta" className="vt-btn-primary vt-cta-glow px-8 py-4 rounded-2xl text-base">
            <Sparkles className="w-5 h-5" /> Analiza tu video gratis con IA
          </a>
          <p className="text-sm text-gray-500 mt-3">3 análisis gratis · sin tarjeta</p>
        </div>
      </main>
    </div>
  );
}
